import { BookOpenTextIcon, CalendarCheckIcon, CalendarXIcon, ClipboardCheckIcon, ClipboardListIcon, FileTextIcon, FolderSearchIcon, LayersIcon, LayoutGridIcon, MessageSquareIcon, ShieldCheckIcon, UsersIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
// BASEBALL READY box mockup: see data/productImage.ts
export { PRODUCT_IMAGE } from './productImage';
export const heroGuarantees = ['Instant digital download', 'One-time payment', 'No subscription', '7-day money-back guarantee'];
export const stats = [{
  value: '240',
  label: 'Pages in the main volumes'
}, {
  value: '6',
  label: 'Digital volumes'
}, {
  value: '120+',
  label: 'Drills by age group'
}, {
  value: '5',
  label: 'Bonuses included'
}, {
  value: '4',
  label: 'Age groups, 5 to 14+'
}];
export const differenceStats = [{
  value: 'Ages 5 to 14+',
  label: 'T-Ball · Coach Pitch · Kid Pitch · Teens'
}, {
  value: '6 digital volumes',
  label: 'From your season plan and practices to hitting, fielding, pitching, game situations and team management'
}, {
  value: 'Print-ready PDF',
  label: 'Use it on your phone or print it for the clipboard in the dugout'
}];
export const beforeItems: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Sunday nights disappear',
  text: 'Hours scrolling for drills and building next week’s practice – and you’re still not sure it works.'
}, {
  title: '“What do we do at practice today?”',
  text: 'You show up at the field with a bucket of balls and no real plan.'
}, {
  title: '12 kids standing in line',
  text: 'One kid hits, eleven wait. Attention drifts, gloves go in the dirt, nobody gets better.'
}, {
  title: 'Parents watching from the bleachers',
  text: 'You feel every eye on you – and every question about playing time and positions.'
}, {
  title: 'Scared of hurting young arms',
  text: 'How many pitches is too many? How much rest? You’re never quite sure.'
}, {
  title: 'Your first season as a volunteer coach',
  text: 'You said yes to help the team – and now you have to run practices and games on your own.'
}];
export const afterItems = ['A 12-week season plan for 4 age groups', '12 practices planned minute by minute (60 / 75 / 90 min)', 'Equipment list and coach script for every practice', '4-station setups that keep every kid moving', 'Hitting, fielding & throwing progressions by age', 'Pitch count and rest charts built around the public MLB Pitch Smart guidelines', '30 game situations with “where does everyone go”', 'Lineup templates, defensive rotations and game-day checklists', 'Print-ready PDF'];
export const recognizeItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: CalendarXIcon,
  title: 'Your evenings go to practice planning',
  text: 'Do you spend nights and weekends figuring out what the team should work on next?'
}, {
  icon: FolderSearchIcon,
  title: 'You never know which drill to use',
  text: 'Are you always searching YouTube for drills that actually fit your players’ age and level?'
}, {
  icon: UsersIcon,
  title: 'Your players are all at different levels',
  text: 'A kid who has never caught a fly ball next to one who already plays travel ball?'
}, {
  icon: ShieldCheckIcon,
  title: 'You worry about young arms',
  text: 'Would you like clear pitch count and rest charts by age – printed and ready for game day?'
}, {
  icon: MessageSquareIcon,
  title: 'Parents have opinions',
  text: 'Would you like a ready parent meeting, a code of conduct and clear rules for playing time?'
}, {
  icon: LayoutGridIcon,
  title: 'You need a clearer structure',
  text: 'Would you like the whole season in one place, so you find the right practice in minutes?'
}];

// Spine colours mirror the physical box (blue, light blue, green, yellow, orange, red).
export const volumes: Array<{
  number: string;
  level: string;
  title: string;
  tagline: string;
  intro: string;
  items: string[];
  outro: string;
  note?: string;
  spine: string;
  rule: string;
}> = [{
  number: 'Volume 01',
  level: 'Ages 5–14+',
  title: 'Season Plan & 12 Ready-to-Run Practices',
  tagline: 'Know exactly what to do at every practice of the season.',
  intro: 'The first volume gives you a complete season plan and twelve practices you can run right away.',
  items: ['12-week season plan: pre-season, regular season, playoffs', '4 age groups: T-Ball 5–6 · Coach Pitch 7–8 · Kid Pitch 9–12 · Teens 13–14+', '12 practices planned minute by minute', '60, 75 and 90-minute practice formats', 'Equipment list, coach script and 4-station setup for each practice', 'Blank practice plan template and skill progression table', 'Coach Quiz A/B with answer key and player evaluation matrix'],
  outro: 'Ready-to-run practices, so you walk onto the field with a clear plan.',
  spine: 'bg-blue-700 text-white',
  rule: 'border-white/30'
}, {
  number: 'Volume 02',
  level: 'Ages 5–14+',
  title: 'Hitting: Drills & Progressions',
  tagline: 'A clear hitting path from the tee to live pitching.',
  intro: 'The second volume breaks the swing into simple steps and gives you drills for every level.',
  items: ['Stance, grip, load and swing path', 'Tee → soft toss → front toss → live', '25+ hitting drills by level', 'Bunting basics', 'Batting cage plan', 'Hitting checklist by age', 'Player cards in 3 levels'],
  outro: 'More swings, less standing around – and hitters who improve.',
  spine: 'bg-sky-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volume 03',
  level: 'Ages 5–14+',
  title: 'Fielding & Throwing',
  tagline: 'Players who field the ball – and throw it to the right base.',
  intro: 'The third volume teaches fielding and throwing in small, logical steps with fun, fast drills.',
  items: ['Ready position, grounders, fly balls and footwork', 'Infield by position and outfield basics', 'Glove work for T-Ball', 'Throwing mechanics: grip, 4-seam, step, follow-through', 'Long toss and throwing programs by age', 'General warm-up routine', '30+ drills and a checklist by position'],
  outro: 'Fielding and throwing that finally click, practice after practice.',
  spine: 'bg-green-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Volume 04',
  level: 'Ages 7–14+',
  title: 'Pitching & Catching Basics',
  tagline: 'Teach pitching step by step – with the pitch counts in front of you.',
  intro: 'The fourth volume covers pitching mechanics, bullpen plans and catcher basics, with pitch count and rest charts built around the public MLB Pitch Smart guidelines.',
  items: ['Pitching mechanics step by step', 'Bullpen plans', 'Pitch count and rest charts by age', 'Printable pitch count tracker', 'Catcher basics: stance, receiving, blocking', 'Catcher gear checklist', 'When a player is ready to pitch', '20+ drills'],
  outro: 'Pitchers and catchers who learn the right way – at the right pace.',
  note: 'General coaching information only. Always follow your league’s rules, and if a player has pain, stop and consult a medical professional.',
  spine: 'bg-yellow-400 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volume 05',
  level: 'Ages 5–14+',
  title: 'Baserunning, Game Situations & Team Defense',
  tagline: 'Every player knows where to go when the ball is hit.',
  intro: 'The fifth volume turns game situations into simple, printable plays your team can practice.',
  items: ['Running to first, turns and leads (when your league allows)', 'Safe sliding and base coach signals', '30 game situations: “where does everyone go”', 'Cutoffs and relays', 'Rundowns and bunt defense', 'Small-ball basics', 'Situation quizzes + answer keys'],
  outro: 'Less confusion on the field – and smarter baseball from every kid.',
  spine: 'bg-orange-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volume 06',
  level: 'Ages 5–14+',
  title: 'Team Management: Lineups, Game Day & Parents',
  tagline: 'Run the team, the dugout and the parents – calmly.',
  intro: 'The sixth volume gives you the tools to organize your team, run game day and communicate with families.',
  items: ['Player evaluation and draft sheets', 'Lineup and batting order templates', 'Defensive rotation chart – everybody plays', 'Game-day checklist and minute-by-minute plan', 'Dugout rules and scorekeeping basics', '20 fun practice games', 'Parent letter, parent meeting and codes of conduct', '100 ready-made player comments and end-of-season awards'],
  outro: 'A well-run team that parents notice.',
  note: 'BASEBALL READY™ is an independent coaching resource. It is not affiliated with or endorsed by MLB, Little League, USA Baseball or any league or organization.',
  spine: 'bg-red-600 text-white',
  rule: 'border-white/30'
}];
export const volumesIntro = {
  title: '6 digital volumes for youth baseball coaches',
  text: 'A complete coaching system that helps you plan and run every practice, all season long. Each volume has 40 pages of ready-to-run practices, drills, field diagrams and checklists.'
};
export const packItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: BookOpenTextIcon,
  title: 'Practices planned minute by minute',
  text: 'No more blank pages. Clear plans with timings, equipment and a coach script get you started fast.'
}, {
  icon: LayersIcon,
  title: '4 age groups',
  text: 'T-Ball, Coach Pitch, Kid Pitch and Teens – the right drill for the right age.'
}, {
  icon: ClipboardListIcon,
  title: 'Field diagrams & stations',
  text: 'Station setups and positions explained clearly, so every kid knows where to stand.'
}, {
  icon: FileTextIcon,
  title: '120+ drills',
  text: 'Hitting, fielding, throwing, pitching, catching and baserunning drills by level.'
}, {
  icon: ClipboardCheckIcon,
  title: 'Pitch count charts & checklists',
  text: 'Pitch count and rest charts, game-day checklists and coach checklists ready to print.'
}, {
  icon: CalendarCheckIcon,
  title: 'Season plan',
  text: 'A 12-week plan from pre-season to playoffs, so you always know what comes next.'
}];
export const pageTypes = ['Ready-to-run practices', 'Field diagrams', 'Pitch count charts', 'Coach checklists'];
export const bonuses: Array<{
  title: string;
  text: string;
  items: string[];
  benefit: string;
}> = [{
  title: 'Practice Game Builder: 40 Fun Baseball Games',
  text: 'Games and competitions that keep every player moving – nobody standing in line.',
  items: ['40 fun baseball games', 'By age group', 'Equipment and setup for each game', 'Ready to print'],
  benefit: 'Practices kids can’t wait to come back to.'
}, {
  title: 'Parent Communication Toolkit',
  text: 'Ready-made words for every conversation with families, from the welcome to the end of the season.',
  items: ['Welcome letter', 'Parent meeting script', '40 email & text templates', 'Snack & volunteer schedules + parent code of conduct'],
  benefit: 'Professional communication in minutes.'
}, {
  title: 'Printable Field Diagram & Lineup Pack',
  text: 'Blank diamonds and team sheets for every practice and every game.',
  items: ['Blank field diagrams', 'Lineup cards', 'Defensive rotation sheets', 'Depth charts'],
  benefit: 'Game day organized on one clipboard.'
}, {
  title: 'Rainy Day & Indoor Practice Kit',
  text: 'When the field is closed, practice still happens.',
  items: ['12 indoor / gym practices', 'Rules and baseball IQ sessions', 'Video review guide', 'Rules games'],
  benefit: 'No more lost practices to the weather.'
}, {
  title: 'First Practice Perfect Kit',
  text: 'Everything you need to start the season with confidence.',
  items: ['First practice for each age group', 'Placement / tryout evaluation', 'First parent meeting', 'Equipment checklist'],
  benefit: 'Your season starts on the right foot.'
}];
export const steps: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Open BASEBALL READY™',
  text: 'Download the package and open the volume that fits your team.'
}, {
  title: 'Pick the age group and practice',
  text: 'Find the right practice, drill or game situation for your players.'
}, {
  title: 'Prepare in 5 minutes',
  text: 'Read the coach script and equipment list and get the bucket and cones ready.'
}, {
  title: 'Print and coach',
  text: 'Print the plan for your clipboard or use it on your phone – and walk onto the field with a clear plan.'
}];
export const audienceItems = ['Coach a rec league, Little League-age, travel or school team', 'Coach players from 5 to 14+', 'Are a parent or volunteer coaching for the first time', 'Want a ready-made season plan', 'Have players at many different levels', 'Are tired of planning every practice from scratch', 'Want clear pitch count and rest charts by age', 'Want your Sunday nights back'];
export const timeValueTasks = ['Searching YouTube for drills', 'Planning each practice', 'Drawing field diagrams and stations', 'Making lineups and defensive rotations', 'Writing messages to parents'];
export const offerRows: Array<{
  label: string;
  detail: string;
}> = [{
  label: '6 digital volumes – 240 pages',
  detail: 'Season plan & practices, hitting, fielding & throwing, pitching & catching, game situations and team management'
}, {
  label: '5 bonuses at no extra cost',
  detail: 'Practice Game Builder, Parent Toolkit, Field Diagram & Lineup Pack, Rainy Day Kit and First Practice Kit'
}, {
  label: 'Ages 5 to 14+',
  detail: 'T-Ball, Coach Pitch, Kid Pitch and Teens – ready-to-run practices, drills and checklists'
}];
export const offerIncludes = ['Instant digital download', 'PDF files you can keep', 'Print-ready', 'No physical product is shipped'];
export const PRICE_AMOUNT = '$19';
export const PRICE_CURRENCY = 'USD';
export const PRICE = `${PRICE_AMOUNT} ${PRICE_CURRENCY}`;
export const PRICE_BEFORE = '$97';

// Only add authentic reviews of BASEBALL READY. The section stays hidden while empty.
export const testimonials: Array<{
  quote: string;
  name: string;
  location: string;
}> = [];
export const faqs = [{
  q: 'How do I get the material?',
  a: 'BASEBALL READY™ is delivered digitally. Right after your purchase you get access to the PDF files, which you can download and keep. Use them on your computer, tablet or phone and print only the pages you need.'
}, {
  q: 'Which ages does it cover?',
  a: 'Players from 5 to 14+, in four age groups: T-Ball (5–6), Coach Pitch (7–8), Kid Pitch (9–12) and Teens (13–14+).'
}, {
  q: 'I’m a parent volunteer with no coaching experience. Is this for me?',
  a: 'Yes. Every practice comes with an equipment list, a minute-by-minute plan and a coach script, so you know exactly what to say and do – even in your first season.'
}, {
  q: 'Is it official or tied to a league?',
  a: 'No. BASEBALL READY™ is an independent coaching resource. It is not affiliated with or endorsed by MLB, Little League, USA Baseball or any league. Always follow your league’s rules.'
}, {
  q: 'What about pitch counts and young arms?',
  a: 'Volume 4 includes pitch count and rest charts by age built around the public MLB Pitch Smart guidelines, plus a printable pitch count tracker. It is general coaching information, not medical advice: always follow your league’s rules, and if a player has pain, stop and consult a medical professional.'
}, {
  q: 'How do I keep 12 kids busy with only one helper?',
  a: 'Practices are built around 4 stations with 1 coach and 2 parent helpers, and the Practice Game Builder bonus adds 40 games – so nobody spends the practice standing in line.'
}, {
  q: 'Can I really prepare a practice in 5 minutes?',
  a: 'BASEBALL READY™ is designed to make preparation much faster. You open a volume, pick a ready-to-run practice and read through it in about five minutes. Time can vary with your team and how much you adapt it.'
}, {
  q: 'Does it cover a whole season?',
  a: 'Yes. Volume 1 includes a 12-week season plan (pre-season, regular season and playoffs) and 12 practices planned minute by minute, and the other volumes give you drills and material for every week.'
}, {
  q: 'How are field diagrams shown?',
  a: 'Clean top-down diamonds with the stations and positions explained in text and tables next to them (P, C, 1B, 2B, 3B, SS, LF, CF, RF), so they are easy to read on the field.'
}, {
  q: 'Are the five bonuses included?',
  a: `Yes. All five bonuses are included at no extra cost – everything for ${PRICE}.`
}, {
  q: 'Is this a subscription?',
  a: 'No. You pay once and get access to the digital material. No recurring charges.'
}, {
  q: 'Can I use it on my phone or tablet?',
  a: 'Yes. The PDF files open on computers, tablets and phones, so you can have the practice plan with you at the field.'
}, {
  q: 'Is there a guarantee?',
  a: 'Yes. BASEBALL READY™ comes with a 7-day money-back guarantee. If the material doesn’t meet your expectations, contact us within 7 days of your purchase and ask for a refund, according to the guarantee terms.'
}];
export const footerLinks = ['Terms of purchase', 'Privacy', 'Refunds', 'Contact'];
